import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { firestore } from '../config/firebaseConfig';
import { doc, getDoc, updateDoc, deleteDoc, collection, getDocs } from 'firebase/firestore';
import './ProfessoresDetails.css';
import CursosDoProfessor from '../components/CursosDoProfessor';
import ConfirmationModal from '../components/ConfirmationModal';

function ProfessoresDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [professor, setProfessor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    areaAtuacao: '',
    linkedin: '',
    fotoUrl: '',
    miniBio: ''
  });
  const [showModal, setShowModal] = useState(false);
  const [professores, setProfessores] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchProfessores = async () => {
      try {
        const querySnapshot = await getDocs(collection(firestore, 'professores'));
        const professoresList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setProfessores(professoresList);
        setCurrentIndex(professoresList.findIndex(prof => prof.id === id));
      } catch (error) {
        console.error('Erro ao buscar professores:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfessores();
  }, [id]);

  useEffect(() => {
    const fetchProfessor = async () => {
      setLoading(true);
      try {
        const docRef = doc(firestore, 'professores', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const professorData = docSnap.data();
          setProfessor({ id: docSnap.id, ...professorData });
          setFormData(professorData);
        } else {
          console.log('Nenhum documento encontrado!');
        }
      } catch (error) {
        console.error('Erro ao buscar professor:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfessor();
  }, [id]);

  const handleEditToggle = () => setIsEditing(prev => !prev);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const docRef = doc(firestore, 'professores', id);
      const updatedData = { ...formData, dataAtual: new Date().toISOString() };
      await updateDoc(docRef, updatedData);
      setProfessor(prev => ({ ...prev, ...updatedData }));
      setIsEditing(false);
    } catch (error) {
      console.error('Erro ao atualizar professor:', error);
    }
  };

  const calculateCompletionPercentage = () => {
    const relevantFields = [
      'nome',
      'email',
      'telefone',
      'areaAtuacao',
      'linkedin',
      'fotoUrl',
      'miniBio'
    ];
    const totalFields = relevantFields.length;
    const filledFields = relevantFields.filter(field => formData[field]?.trim() !== '').length;
    return Math.round((filledFields / totalFields) * 100);
  };

  const handleDelete = async () => {
    try {
      await deleteDoc(doc(firestore, 'professores', id));
      navigate('/professores');
    } catch (error) {
      console.error('Erro ao excluir professor:', error);
    } finally {
      setShowModal(false);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      const previousId = professores[currentIndex - 1].id;
      navigate(`/professores/${previousId}`);
      setCurrentIndex(currentIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < professores.length - 1) {
      const nextId = professores[currentIndex + 1].id;
      navigate(`/professores/${nextId}`);
      setCurrentIndex(currentIndex + 1);
    }
  };

  if (loading) return <p>Carregando...</p>;

  const completionPercentage = calculateCompletionPercentage();

  return (
    <div className="professor-details">
      {professor ? (
        <>
          {isEditing ? (
            <form onSubmit={handleFormSubmit} className="form-edit">
              <h1>Editar Professor</h1>
              <div className="form-row">
                <div className="form-photo">
                  <label>
                    URL da Foto:
                    <input
                      type="url"
                      name="fotoUrl"
                      value={formData.fotoUrl || ''}
                      onChange={handleInputChange}
                    />
                  </label>
                  {formData.fotoUrl && <img src={formData.fotoUrl} alt="Foto do Professor" className="professor-photo" />}
                </div>
                <div className="form-info">
                  <label>
                    Nome:
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome || ''}
                      onChange={handleInputChange}
                    />
                  </label>
                  <label>
                    Email:
                    <input
                      type="email"
                      name="email"
                      value={formData.email || ''}
                      onChange={handleInputChange}
                    />
                  </label>
                  <label>
                    Telefone:
                    <input
                      type="text"
                      name="telefone"
                      value={formData.telefone || ''}
                      onChange={handleInputChange}
                    />
                  </label>
                  <label>
                    Área de Atuação:
                    <input
                      type="text"
                      name="areaAtuacao"
                      value={formData.areaAtuacao || ''}
                      onChange={handleInputChange}
                    />
                  </label>
                </div>
              </div>
              <label>
                LinkedIn:
                <input
                  type="url"
                  name="linkedin"
                  value={formData.linkedin || ''}
                  onChange={handleInputChange}
                />
              </label>
              <label>
                Mini Bio:
                <textarea
                  name="miniBio"
                  value={formData.miniBio || ''}
                  onChange={handleInputChange}
                />
              </label>
              <div className="form-actions">
                <button type="submit">Salvar</button>
                <button type="button" onClick={handleEditToggle}>Cancelar</button>
              </div>
            </form>
          ) : (
            <>
              <div className="professor-header">
                <h1>{professor.nome}</h1>
                {professor.fotoUrl && <img src={professor.fotoUrl} alt="Foto do Professor" className="professor-photo" />}
              </div>
              <div className="completion-bar">
                <div className="completion-bar-filled" style={{ width: `${completionPercentage}%` }}>
                  {completionPercentage}%
                </div>
              </div>
              <div className="professor-info">
                <div className="info-item">
                  <h3>{professor.areaAtuacao}</h3>
                </div>
                <div className="info-item">
                  {professor.miniBio}
                </div>
                <div className="info-item">
                  <strong>Email:</strong> {professor.email}
                </div>
                <div className="info-item">
                  <strong>Telefone:</strong> {professor.telefone}
                </div>
                <div className="info-item">
                  <strong>LinkedIn:</strong> <a href={professor.linkedin} target="_blank" rel="noopener noreferrer">{professor.linkedin}</a>
                </div>
                <CursosDoProfessor professorId={professor.id} />
              </div>
              <div className="button-container">
                <button className="delete-button" onClick={() => setShowModal(true)}>Excluir</button>
                <button className="edit-button" onClick={handleEditToggle}>Editar</button>
                <button className="back-button" onClick={() => navigate('/professores')}>Voltar</button>
              </div>
            </>
          )}
        </>
      ) : (
        <p>Professor não encontrado.</p>
      )}

      {showModal && (
        <ConfirmationModal
          message="Tem certeza que deseja excluir este professor?"
          onConfirm={handleDelete}
          onCancel={() => setShowModal(false)}
        />
      )}

      {/* Controles de Navegação */}
      <div className="nav-buttons">
        <button className="nav-button" onClick={goToPrevious} disabled={currentIndex <= 0}>
          Anterior
        </button>
        <button className="nav-button" onClick={goToNext} disabled={currentIndex >= professores.length - 1}>
          Próximo
        </button>
      </div>
    </div>
  );
}

export default ProfessoresDetails;
